import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, MessageSquare, Plus, Search, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PhoneLink, EmailLink } from "../components/ContactLinks";

/**
 * Agent-side Messages tab.
 *
 * Data model reminder:
 *   Thread key = (client_id, transaction_id?)
 *   transaction_id = null → the "General" thread with that client (no deal).
 *
 * Layout:
 *   • md+           → split pane: inbox list left, thread right (fixed heights,
 *                     each side scrolls independently).
 *   • <md (mobile)  → inbox list full-width; tapping a thread swaps to the
 *                     thread view with a back button.
 */

const AGENT_KEY = "realtor_os_portal_agent";  // reserved for future use

function threadKey(t) {
  return `${t.client_id || ""}::${t.transaction_id || "general"}`;
}

export default function Messages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const endRef = useRef(null);

  // -----------------------------------------------------------------------
  // Loaders
  // -----------------------------------------------------------------------
  const loadThreads = async () => {
    const { data } = await api.get("/messages");
    setThreads(data || []);
    if ((data || []).length && !active && typeof window !== "undefined" && window.innerWidth >= 768) {
      const t = data[0];
      setActive({
        transaction_id: t.transaction_id,
        client_id: t.client_id,
        client_name: t.client_name,
        is_general: t.is_general,
      });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadThreads(); }, []);

  const loadThread = async () => {
    if (!active?.client_id) return;
    const params = { client_id: active.client_id };
    if (active.transaction_id) params.transaction_id = active.transaction_id;
    const { data } = await api.get("/messages/thread", { params });
    setThread(data || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadThread(); }, [active?.client_id, active?.transaction_id]);

  // Poll for new incoming messages so the agent sees replies without a refresh.
  useEffect(() => {
    if (!active?.client_id) return;
    const id = setInterval(() => { loadThread(); loadThreads(); }, 15_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.client_id, active?.transaction_id]);

  // Auto-scroll to bottom of thread whenever it grows.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread.length]);

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------
  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text || !active?.client_id) return;
    try {
      const payload = { client_id: active.client_id, body: text };
      if (active.transaction_id) payload.transaction_id = active.transaction_id;
      await api.post("/messages", payload);
      setBody("");
      loadThread(); loadThreads();
    } catch { toast.error("Send failed"); }
  };

  // Load clients on-demand when the picker opens.
  const openPicker = async () => {
    setPickerOpen(true);
    if (clients.length === 0) {
      try {
        const { data } = await api.get("/clients");
        setClients(Array.isArray(data) ? data : []);
      } catch { toast.error("Could not load your clients"); }
    }
  };

  const startGeneralThread = (c) => {
    setActive({
      transaction_id: null,
      client_id: c.id,
      client_name: c.full_name,
      is_general: true,
    });
    setPickerOpen(false);
    setQ("");
    // Prepend an optimistic empty thread entry so it appears in the sidebar
    // immediately even before any message is sent.
    setThreads((prev) => {
      const key = `${c.id}::general`;
      const exists = prev.some(t => threadKey(t) === key);
      if (exists) return prev;
      return [{
        transaction_id: null,
        client_id: c.id,
        client_name: c.full_name,
        last_body: "",
        last_sender: "agent",
        last_created_at: new Date().toISOString(),
        is_general: true,
      }, ...prev];
    });
  };

  const filteredClients = useMemo(() => {
    if (!q.trim()) return clients;
    const needle = q.trim().toLowerCase();
    return clients.filter(c =>
      (c.full_name || "").toLowerCase().includes(needle) ||
      (c.email || "").toLowerCase().includes(needle) ||
      (c.phone || "").toLowerCase().includes(needle),
    );
  }, [clients, q]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Messages</h1>
        <button
          type="button"
          onClick={openPicker}
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 text-sm font-semibold shadow-sm"
          data-testid="msg-new-btn"
        >
          <Plus className="h-4 w-4" /> New Message
        </button>
      </div>

      <div className="md:grid md:grid-cols-[300px_1fr] md:gap-4 rounded-lg border border-slate-200 bg-white overflow-hidden md:h-[70vh] min-h-[70vh]">
        {/* Inbox list */}
        <aside className={`${active ? "hidden md:flex" : "flex"} flex-col md:border-r border-slate-200 overflow-hidden`}>
          <div className="overflow-y-auto divide-y divide-slate-100">
            {threads.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="msg-empty">
                <MessageSquare className="h-8 w-8 mx-auto text-slate-300" />
                <p className="mt-3">No messages yet. Tap <span className="font-semibold">New Message</span> to start a thread with any client.</p>
              </div>
            )}
            {threads.map((t) => {
              const key = threadKey(t);
              const activeKey = active ? threadKey(active) : null;
              const isActive = key === activeKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActive({
                    transaction_id: t.transaction_id,
                    client_id: t.client_id,
                    client_name: t.client_name,
                    is_general: t.is_general,
                  })}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors ${isActive ? "bg-slate-50" : ""}`}
                  data-testid={`msg-thread-${t.client_id}-${t.transaction_id || "general"}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-slate-900 text-sm truncate flex-1">{t.client_name || "(unknown)"}</div>
                    {t.is_general && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 shrink-0">General</span>
                    )}
                  </div>
                  {t.last_body && (
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {t.last_sender === "agent" ? "You: " : ""}{t.last_body}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400 mt-1 tabular-nums">
                    {new Date(t.last_created_at).toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Thread pane */}
        <section className={`${active ? "flex" : "hidden md:flex"} flex-col min-h-0`}>
          {active && (
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-white">
              <button
                onClick={() => setActive(null)}
                className="md:hidden h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center"
                aria-label="Back"
                data-testid="msg-back-btn"
              >
                <ArrowLeft className="h-4 w-4 text-slate-700" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-slate-900 text-sm sm:text-base truncate">{active.client_name}</div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  {active.is_general ? "General thread" : "Transaction thread"}
                </div>
              </div>
            </div>
          )}

          {/* Scroll-locked body */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 bg-slate-50" data-testid="msg-thread-body">
            {active ? (
              thread.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-16">
                  No messages yet. Say hello to {active.client_name?.split(" ")[0] || "your client"}.
                </div>
              ) : (
                <div className="space-y-2">
                  {thread.map((m, i) => {
                    const isMine = m.sender === "agent";
                    const prev = thread[i - 1];
                    const startOfRun = !prev || prev.sender !== m.sender;
                    return (
                      <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"} ${startOfRun ? "mt-3" : ""}`}>
                        <div className={`max-w-[80%] sm:max-w-md rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                          isMine ? "bg-slate-900 text-white rounded-br-md" : "bg-white text-slate-900 border border-slate-200 rounded-bl-md"
                        }`}>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`text-[10px] mt-1 tabular-nums ${isMine ? "text-white/60" : "text-slate-400"}`}>
                            {new Date(m.created_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
              )
            ) : (
              <div className="hidden md:flex h-full items-center justify-center text-sm text-slate-500">
                Pick a thread on the left, or start a new one.
              </div>
            )}
          </div>

          {active && (
            <form onSubmit={send} className="border-t border-slate-200 p-3 bg-white flex items-center gap-2 shrink-0">
              <input
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder={`Reply to ${active.client_name?.split(" ")[0] || "…"}`}
                className="flex-1 rounded-full border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:border-slate-900"
                data-testid="msg-body-input"
              />
              <button
                type="submit"
                disabled={!body.trim()}
                className="h-10 w-10 rounded-full flex items-center justify-center text-white shadow-md disabled:opacity-40 shrink-0"
                style={{ background: "var(--primary-hex)" }}
                data-testid="msg-send-btn"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          )}
        </section>
      </div>

      {/* Client picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-slate-950/50 backdrop-blur-sm px-4 py-8 sm:py-16" onClick={() => setPickerOpen(false)}>
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
              <Search className="h-4 w-4 text-slate-500" />
              <input
                autoFocus
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by name, email, or phone…"
                className="flex-1 border-0 outline-none text-sm placeholder:text-slate-400"
                data-testid="msg-picker-search"
              />
              <button onClick={() => setPickerOpen(false)} className="h-8 w-8 rounded hover:bg-slate-100 flex items-center justify-center" aria-label="Close" data-testid="msg-picker-close">
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
              {filteredClients.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-500">
                  {clients.length === 0 ? "Loading your clients…" : "No matches. Try a different name."}
                </div>
              )}
              {filteredClients.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => startGeneralThread(c)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                  data-testid={`msg-picker-client-${c.id}`}
                >
                  <div className="font-semibold text-slate-900 text-sm">{c.full_name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {c.email && <EmailLink email={c.email} testId={`msg-picker-email-${c.id}`} stop />}
                    {c.phone && <PhoneLink phone={c.phone} testId={`msg-picker-phone-${c.id}`} stop />}
                    {!c.email && !c.phone && <span className="text-slate-500">No contact on file</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
